import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'
import { StringToHtml } from '@bonniernews/dn-design-system-web/helpers/stringToHtml.tsx'

import { LogoWithPayoff } from './logoWithPayoff'

interface FooterLink extends SharedProps {
  href: string
  text: string
}

interface FooterLinkList {
  attributes?: object
  title?: string
  links: FooterLink[]
}

interface Footerbutton extends SharedProps {
  text: string
}

export interface FooterProps extends SharedProps {
  editorsHtml?: string
  copyright?: string
  linkGroups?: FooterLinkList[]
  rudolf: {
    imgUrl: string
    text: string
  }
  buttons?: Footerbutton[]
  forcePx?: boolean
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/footer](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/footer)
 * - Storybook: [footer](https://designsystem.dn.se/?path=/docs/page-footer--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/footer/footer.scss'`
 */
export const Footer = ({
  editorsHtml,
  copyright,
  linkGroups,
  rudolf,
  buttons,
  classNames,
  attributes,
}: FooterProps) => {
  const componentClassName = 'ds-footer'
  const classes = formatClassString([componentClassName, classNames])

  return (
    <footer className={classes} {...attributes}>
      <div className={`${componentClassName}__inner`}>
        <div className={`${componentClassName}__link-grid`}>
          {linkGroups &&
            linkGroups.map((group) => (
              <div
                className={`${componentClassName}__link-group`}
                {...group.attributes}
                key={`footer-group-${group.title}`}
              >
                {group.title && <h2>{group.title}</h2>}
                <ul>
                  {group.links.map((link, index) => (
                    <li key={`group-link-${link.href}-${index}`}>
                      <a
                        href={link.href}
                        className={`ds-footer__link ${link.classNames ? link.classNames : ''}`}
                        {...link.attributes}
                      >
                        {link.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </div>

        <div className={`${componentClassName}__editors`}>
          {editorsHtml && <StringToHtml htmlString={editorsHtml} />}
          {copyright && (
            <p className={`${componentClassName}__copyright`} dangerouslySetInnerHTML={{ __html: copyright }}></p>
          )}
        </div>

        {rudolf && (
          <div className={`${componentClassName}__rudolf`}>
            <img src={rudolf.imgUrl} alt='Rudolf Wall' width='64' height='64' loading='lazy' />
            <p>{rudolf.text}</p>
          </div>
        )}
        <div className={`${componentClassName}__logo`}>
          <LogoWithPayoff />
        </div>
        {buttons && (
          <div className={`${componentClassName}__buttons`}>
            {buttons.map((button) => (
              <button
                className={`ds-footer__button ${button.classNames ? button.classNames : ''}`}
                {...button.attributes}
              >
                {button.text}
              </button>
            ))}
          </div>
        )}
      </div>
    </footer>
  )
}

export default Footer
